import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Panel } from "primereact/panel";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Accordion, AccordionTab } from "primereact/accordion";

import { useTenantOptions } from "../../../hooks/useTenantOptions";
import { useTenantScope } from "../../../hooks/useTenantScope";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import { useGetEventTypesQuery, useGetEventSubTypesQuery } from "../../../store/services/events-types";
import type {
  FeaturedBadgeRule,
  FeaturedBadgeRuleEntryValues,
  FeaturedBadgeRuleFormValues,
} from "../../../models/rulesFeaturedBadge";
import { FEATURED_BADGE_STATES } from "../../../models/rulesFeaturedBadge";
import {
  canRemoveFeaturedBadgeEntry,
  createFeaturedBadgeEntry,
  FEATURED_BADGE_LABEL_MAX_LENGTH,
  FEATURED_BADGE_ALL_TENANTS_LABEL,
  findFeaturedBadgeRuleConflict,
  formatFeaturedBadgeLabelMaxLengthError,
  getEntryLabelLengthMessage,
  getEntrySlugConflictMessage,
  getEntryThresholdBounds,
  getFeaturedBadgeDefaultLabel,
  getFeaturedBadgeEntryRemovalBlockReason,
  getNextAvailableFeaturedBadgeSlug,
  getThresholdBoundsHint,
  FEATURED_BADGE_DEFAULT_PRESETS,
  isFeaturedBadgeClosedSlug,
  sortFeaturedBadgeEntriesByUrgency,
} from "../configs/featuredBadgeRuleHelpers";
import {
  FEATURED_BADGE_COLOR_VARIANT_OPTIONS,
  getFeaturedBadgeSwatchesForTenant,
  getFeaturedBadgeSwatch,
} from "../configs/featuredBadgeColorPalette";
import { isFeaturedBadgeColorSlug } from "../../../models/featuredBadgeColorPalette";
import type { FeaturedBadgeColorSlug } from "../../../models/featuredBadgeColorPalette";
import {
  ALL_RULES_TENANTS_FILTER_ID,
  buildRulesTenantOptions,
  findGrupsTenantIdFromList,
  fromDropdownTenantId,
  GRUPS_RULES_TENANT_ID,
  ownedRulesTenantIdForAdmin,
  toDropdownTenantId,
} from "../constants/rulesTenant";
import type { RulesInlineFormMode } from "./RulesInlineFormPanel";
import { ThresholdDurationPicker } from "./ThresholdDurationPicker";

interface FeaturedBadgeRulesFormPanelProps {
  mode: RulesInlineFormMode;
  values: FeaturedBadgeRuleFormValues;
  onChange: (next: FeaturedBadgeRuleFormValues) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  existingRows?: FeaturedBadgeRule[];
  editingScopeRowIds?: number[];
  onDelete?: () => void;
  isDeletingGroup?: boolean;
  editingRowId?: number | null; // Keeps signature compatibility, although we use entry.id
  grupsTenantId?: string;
}

const formatFieldLabel = (label: string, required?: boolean): string =>
  required ? `${label} *` : label;

const updateEntryAtIndex = (
  entries: FeaturedBadgeRuleEntryValues[],
  index: number,
  patch: Partial<FeaturedBadgeRuleEntryValues>
): FeaturedBadgeRuleEntryValues[] =>
  entries.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...patch } : entry));

export const FeaturedBadgeRulesFormPanel: React.FC<FeaturedBadgeRulesFormPanelProps> = ({
  mode,
  values,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
  errorMessage,
  existingRows = [],
  editingScopeRowIds = [],
  onDelete,
  isDeletingGroup = false,
  grupsTenantId: grupsTenantIdProp,
}) => {
  const focusedTabRef = useRef<HTMLDivElement | null>(null);
  const [activeAccordionIndexes, setActiveAccordionIndexes] = useState<number[]>([0]);
  const [highlightedEntryKey, setHighlightedEntryKey] = useState<string | null>(null);
  const [labelLimitAttemptedKeys, setLabelLimitAttemptedKeys] = useState<Set<string>>(
    () => new Set()
  );
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const { data: eventTypes, isLoading: isLoadingTypes } = useGetEventTypesQuery();
  const { data: eventSubTypes, isLoading: isLoadingSubTypes } = useGetEventSubTypesQuery();
  const tenantScope = useTenantScope();
  const { canSeeAllTenants } = useTenantOptions();
  const ownedTenantId = ownedRulesTenantIdForAdmin(tenantScope.role, tenantScope.tenantId);

  const tenantOptions = useMemo(
    () =>
      buildRulesTenantOptions(
        Array.isArray(tenants) ? tenants : [],
        canSeeAllTenants && !tenantScope.locked,
        {
          id: ALL_RULES_TENANTS_FILTER_ID,
          provider: FEATURED_BADGE_ALL_TENANTS_LABEL,
        }
      ),
    [canSeeAllTenants, tenantScope.locked, tenants]
  );
  const grupsTenantId = grupsTenantIdProp ?? findGrupsTenantIdFromList(tenants);
  const selectedTenantId =
    values.tenantId === ALL_RULES_TENANTS_FILTER_ID
      ? ALL_RULES_TENANTS_FILTER_ID
      : toDropdownTenantId(values.tenantId, grupsTenantId);

  useEffect(() => {
    if (!ownedTenantId || values.tenantId === ownedTenantId) {
      return;
    }

    onChange({ ...values, tenantId: ownedTenantId });
  }, [onChange, ownedTenantId, values]);

  const eventTypeOptions = useMemo(() => {
    return [{ eventTypeId: undefined, eventTypeName: "Todos los tipos (General)" }, ...(Array.isArray(eventTypes) ? eventTypes : [])];
  }, [eventTypes]);

  const selectedTenantTheme = useMemo(() => {
    if (values.tenantId === ALL_RULES_TENANTS_FILTER_ID) {
      return undefined;
    }

    const allTenants = Array.isArray(tenants) ? tenants : [];
    const resolvedTenantId =
      values.tenantId === GRUPS_RULES_TENANT_ID ? grupsTenantId : values.tenantId;
    const found = allTenants.find((t) => t.id === resolvedTenantId);
    return found?.theme;
  }, [grupsTenantId, tenants, values.tenantId]);

  const colorSwatches = useMemo(
    () => getFeaturedBadgeSwatchesForTenant(selectedTenantTheme),
    [selectedTenantTheme]
  );

  const eventSubTypeOptions = useMemo(() => {
    const allSubTypes = Array.isArray(eventSubTypes) ? eventSubTypes : [];
    const filtered = values.eventTypeId 
      ? allSubTypes.filter(st => st.eventTypeId === values.eventTypeId)
      : [];
    return [{ eventSubTypeId: null, eventSubTypeName: "Todos los subtipos (General)" }, ...filtered];
  }, [eventSubTypes, values.eventTypeId]);

  const panelHeader = mode === "edit" ? "Editar regla" : mode === "create" ? "Nueva regla" : "";
  const canManageMultipleEntries = mode === "create" || mode === "edit";
  const hasRemovableEntries =
    canManageMultipleEntries &&
    values.entries.length > 1 &&
    values.entries.some((_, index) => canRemoveFeaturedBadgeEntry(values.entries, index));

  const excludedConflictRowIds = useMemo(() => {
    if (editingScopeRowIds.length > 0) {
      return editingScopeRowIds;
    }

    return values.entries.map((entry) => entry.id).filter((id): id is number => id != null);
  }, [editingScopeRowIds, values.entries]);

  const ruleConflict = useMemo(() => {
    if (isSubmitting) {
      return null;
    }

    return findFeaturedBadgeRuleConflict(
      existingRows,
      values,
      excludedConflictRowIds,
      grupsTenantId
    );
  }, [existingRows, values, excludedConflictRowIds, isSubmitting, grupsTenantId]);

  const entriesCount = values.entries.length;
  useEffect(() => {
    // Abrir todos los acordeones cuando cambia la cantidad de etiquetas.
    setActiveAccordionIndexes(Array.from({ length: entriesCount }, (_, index) => index));
  }, [entriesCount]);

  useEffect(() => {
    if (!highlightedEntryKey) {
      return;
    }

    const entryKey = highlightedEntryKey;
    const highlightTimer = window.setTimeout(() => {
      focusedTabRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

      const headerElement = document.getElementById(`featuredBadgeAccordionHeader-${entryKey}`);
      headerElement?.scrollIntoView({ behavior: "smooth", block: "nearest" });

      const labelInput = document.getElementById(`featuredBadgeLabel-${entryKey}`);
      labelInput?.focus();

      if (focusedTabRef.current) {
        focusedTabRef.current.classList.add("surface-highlight-pulse");
        window.setTimeout(() => {
          focusedTabRef.current?.classList.remove("surface-highlight-pulse");
        }, 2000);
      }

      setHighlightedEntryKey(null);
    }, 0);

    return () => window.clearTimeout(highlightTimer);
  }, [highlightedEntryKey, values.entries]);

  if (mode === "hidden") {
    return null;
  }

  const handleTenantChange = ({ value }: DropdownChangeEvent) => {
    if (value === ALL_RULES_TENANTS_FILTER_ID) {
      onChange({
        ...values,
        tenantId: ALL_RULES_TENANTS_FILTER_ID,
      });
      return;
    }

    onChange({
      ...values,
      tenantId: fromDropdownTenantId(value as string | undefined, grupsTenantId),
    });
  };

  const handleEventTypeChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...values,
      eventTypeId: value ?? undefined,
      eventSubTypeId: null,
    });
  };

  const handleEventSubTypeChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...values,
      eventSubTypeId: value,
    });
  };

  const handleEntrySlugChange =
    (index: number) =>
    ({ value }: DropdownChangeEvent) => {
      const nextSlug = typeof value === "string" ? value : "";
      const currentEntry = values.entries[index];
      const currentEntryKey = currentEntry?.key ?? null;
      const updatedEntries = updateEntryAtIndex(values.entries, index, {
        slug: nextSlug,
        ...(nextSlug === "closed" ? { thresholdMinutes: 0 } : {}),
      });
      const nextEntries = nextSlug
        ? sortFeaturedBadgeEntriesByUrgency(updatedEntries)
        : updatedEntries;

      if (currentEntryKey) {
        setActiveAccordionIndexes(nextEntries.map((_, entryIndex) => entryIndex));
        setHighlightedEntryKey(currentEntryKey);
      }

      onChange({
        ...values,
        entries: nextEntries,
      });
    };

  const handleEntryLabelChange =
    (index: number, entryKey: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const label = event.target.value.slice(0, FEATURED_BADGE_LABEL_MAX_LENGTH);
      onChange({
        ...values,
        entries: updateEntryAtIndex(values.entries, index, { label }),
      });
      if (label.length < FEATURED_BADGE_LABEL_MAX_LENGTH) {
        setLabelLimitAttemptedKeys((prev) => {
          if (!prev.has(entryKey)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(entryKey);
          return next;
        });
      }
    };

  const handleEntryLabelKeyDown =
    (entryKey: string, currentLength: number) =>
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (currentLength < FEATURED_BADGE_LABEL_MAX_LENGTH) {
        return;
      }

      const navigationKeys = new Set([
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
        "Home",
        "End",
      ]);
      if (navigationKeys.has(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      if (event.key.length === 1) {
        event.preventDefault();
        setLabelLimitAttemptedKeys((prev) => new Set(prev).add(entryKey));
      }
    };

  const handleEntryLabelPaste =
    (index: number, entryKey: string) =>
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const pasted = event.clipboardData.getData("text");
      const current = values.entries[index]?.label ?? "";
      const start = input.selectionStart ?? current.length;
      const end = input.selectionEnd ?? current.length;
      const nextLength = current.length - (end - start) + pasted.length;

      if (nextLength > FEATURED_BADGE_LABEL_MAX_LENGTH) {
        setLabelLimitAttemptedKeys((prev) => new Set(prev).add(entryKey));
      }
    };

  const handleEntryThresholdChange = (index: number, thresholdMinutes: number) => {
    onChange({
      ...values,
      entries: updateEntryAtIndex(values.entries, index, { thresholdMinutes }),
    });
  };

  const handleEntryColorVariantChange = (index: number, colorVariant: number) => {
    onChange({
      ...values,
      entries: updateEntryAtIndex(values.entries, index, { colorVariant }),
    });
  };

  const handleAddEntry = () => {
    if (ruleConflict) {
      toast.error(ruleConflict);
      return;
    }

    const nextSlug = getNextAvailableFeaturedBadgeSlug(values.entries);
    if (!nextSlug) {
      toast.error(`No se pueden agregar más de ${FEATURED_BADGE_STATES.length} etiquetas.`);
      return;
    }

    const preset = FEATURED_BADGE_DEFAULT_PRESETS.find((entry) => entry.slug === nextSlug);

    onChange({
      ...values,
      entries: sortFeaturedBadgeEntriesByUrgency([
        ...values.entries,
        createFeaturedBadgeEntry(preset ?? { slug: nextSlug }),
      ]),
    });
  };

  const handleRemoveEntry = (index: number) => {
    const blockReason = getFeaturedBadgeEntryRemovalBlockReason(values.entries, index);
    if (blockReason) {
      toast.error(blockReason);
      return;
    }

    onChange({
      ...values,
      entries: values.entries.filter((_, entryIndex) => entryIndex !== index),
    });
  };

  const handleActiveChange = (nextActive: boolean) => {
    onChange({
      ...values,
      active: nextActive,
    });
  };

  return (
    <Panel header={panelHeader} className="mb-4">
      <style>{`
        @keyframes surface-highlight-pulse {
          0% { background-color: var(--highlight-bg, rgba(var(--primary-color-rgb), 0.15)); box-shadow: 0 0 0 2px var(--primary-color); }
          100% { background-color: transparent; box-shadow: transparent; }
        }
        .surface-highlight-pulse {
          animation: surface-highlight-pulse 2s ease-out;
          border-radius: var(--border-radius);
          padding: 1rem;
          margin: -1rem;
        }
      `}</style>
      <div className="grid row-gap-4">
        <div className="col-12">
          <div className="grid column-gap-3 row-gap-3">
            {!tenantScope.hidden && !tenantScope.locked && (
              <div className="col-12 md:col-4">
                <FloatLabel className="w-full">
                  <Dropdown
                    id="featuredBadgeFormTenantId"
                    value={selectedTenantId}
                    onChange={handleTenantChange}
                    options={tenantOptions}
                    optionLabel="provider"
                    optionValue="id"
                    loading={isLoadingTenants}
                    className="w-full p-inputwrapper-filled"
                  />
                  <label htmlFor="featuredBadgeFormTenantId">{formatFieldLabel("Tenant", true)}</label>
                </FloatLabel>
              </div>
            )}
            <div className="col-12 md:col-4">
              <FloatLabel className="w-full">
                <Dropdown
                  id="featuredBadgeFormEventTypeId"
                  value={values.eventTypeId}
                  onChange={handleEventTypeChange}
                  options={eventTypeOptions}
                  optionLabel="eventTypeName"
                  optionValue="eventTypeId"
                  loading={isLoadingTypes}
                  className="w-full p-inputwrapper-filled"
                />
                <label htmlFor="featuredBadgeFormEventTypeId">{formatFieldLabel("Tipo")}</label>
              </FloatLabel>
            </div>
            <div className="col-12 md:col-4">
              <FloatLabel className="w-full">
                <Dropdown
                  id="featuredBadgeFormEventSubTypeId"
                  value={values.eventSubTypeId}
                  onChange={handleEventSubTypeChange}
                  options={eventSubTypeOptions}
                  optionLabel="eventSubTypeName"
                  optionValue="eventSubTypeId"
                  loading={isLoadingSubTypes}
                  disabled={!values.eventTypeId}
                  className="w-full p-inputwrapper-filled"
                />
                <label htmlFor="featuredBadgeFormEventSubTypeId">{formatFieldLabel("Subtipo")}</label>
              </FloatLabel>
            </div>
          </div>
        </div>

        <div className="col-12">
          <Accordion
            multiple
            activeIndex={activeAccordionIndexes}
            onTabChange={(event) => {
              setActiveAccordionIndexes(
                Array.isArray(event.index) ? event.index : [event.index]
              );
            }}
            className="w-full"
          >
            {values.entries.map((entry, index) => {
              const slugConflict = getEntrySlugConflictMessage(values, index);
              const labelSuffix =
                values.entries.length > 1 ? ` en la etiqueta #${index + 1}` : "";
              const labelLengthError = getEntryLabelLengthMessage(entry.label, labelSuffix);
              const labelLimitAttempted = labelLimitAttemptedKeys.has(entry.key);
              const labelErrorMessage =
                labelLengthError ||
                (labelLimitAttempted
                  ? formatFeaturedBadgeLabelMaxLengthError(labelSuffix)
                  : null);
              const canRemoveEntry = canRemoveFeaturedBadgeEntry(values.entries, index);

              const thresholdBounds = getEntryThresholdBounds(values.entries, index);
              const thresholdHint = getThresholdBoundsHint(thresholdBounds);
              const isHighlightedEntry = highlightedEntryKey === entry.key;
              const colorSlug = entry.slug;
              const hasColorPalette =
                Boolean(colorSlug) && isFeaturedBadgeColorSlug(colorSlug ?? "");

              return (
                <AccordionTab 
                  key={entry.key} 
                  header={
                    <span id={`featuredBadgeAccordionHeader-${entry.key}`}>
                      {entry.label || getFeaturedBadgeDefaultLabel(entry.slug ?? "") || `Regla ${index + 1}`}
                    </span>
                  }
                  pt={{
                    content: { className: "py-4 px-3" },
                    headerAction: { className: "surface-50" }
                  }}
                >
                  <div
                    ref={isHighlightedEntry ? focusedTabRef : undefined}
                    className="grid row-gap-4 align-items-start"
                  >
                    <div className="col-12 md:col-3">
                      <FloatLabel className="w-full">
                        <Dropdown
                          id={`featuredBadgeSlug-${entry.key}`}
                          value={entry.slug ?? ""}
                          onChange={handleEntrySlugChange(index)}
                          options={FEATURED_BADGE_STATES.filter(
                            (state) =>
                              state.value === entry.slug ||
                              !values.entries.some(
                                (otherEntry, otherIndex) =>
                                  otherIndex !== index && otherEntry.slug === state.value
                              )
                          )}
                          optionLabel="label"
                          optionValue="value"
                          className="w-full"
                        />
                        <label htmlFor={`featuredBadgeSlug-${entry.key}`}>
                          {formatFieldLabel("Estado", true)}
                        </label>
                      </FloatLabel>
                    </div>

                    <div className={`col-12 ${hasRemovableEntries ? "md:col-5" : "md:col-6"}`}>
                      <FloatLabel className="w-full">
                        <InputText
                          id={`featuredBadgeLabel-${entry.key}`}
                          value={entry.label ?? ""}
                          onChange={handleEntryLabelChange(index, entry.key)}
                          onKeyDown={handleEntryLabelKeyDown(
                            entry.key,
                            entry.label?.length ?? 0
                          )}
                          onPaste={handleEntryLabelPaste(index, entry.key)}
                          maxLength={FEATURED_BADGE_LABEL_MAX_LENGTH}
                          className="w-full"
                        />
                        <label htmlFor={`featuredBadgeLabel-${entry.key}`}>
                          {formatFieldLabel("Texto de la etiqueta", true)}
                        </label>
                      </FloatLabel>
                      {labelErrorMessage && (
                        <small className="text-red-500 line-height-3 block mt-1">
                          {labelErrorMessage}
                        </small>
                      )}
                    </div>

                    <div className="col-12 md:col-3">
                      {isFeaturedBadgeClosedSlug(entry.slug) ? (
                        <div className="flex flex-column gap-2 h-full justify-content-center">
                          <span className="text-sm text-500 line-height-3">
                            Se aplica al cerrar la inscripción del evento (sin umbral).
                          </span>
                        </div>
                      ) : (
                        <ThresholdDurationPicker
                          id={`featuredBadgeThreshold-${entry.key}`}
                          value={entry.thresholdMinutes ?? 0}
                          onChange={(thresholdMinutes) =>
                            handleEntryThresholdChange(index, thresholdMinutes)
                          }
                          className="w-full p-filled"
                          disabled={isSubmitting}
                          minMinutes={thresholdBounds.minMinutes}
                          maxMinutes={thresholdBounds.maxMinutes}
                          hint={thresholdHint}
                          description="Muestra la etiqueta cuando el tiempo restante para el cierre de inscripción sea igual o menor a este valor."
                          label="Umbral"
                          labelRequired
                        />
                      )}
                    </div>

                    {hasRemovableEntries && (
                      <div className="col-12 md:col-1 flex align-items-center justify-content-center">
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          severity="danger"
                          text
                          rounded
                          aria-label="Quitar etiqueta"
                          tooltip={
                            canRemoveEntry
                              ? "Quitar"
                              : "Solo podés quitar el estado menos urgente"
                          }
                          tooltipOptions={{ position: "top" }}
                          onClick={() => handleRemoveEntry(index)}
                          disabled={isSubmitting || !canRemoveEntry}
                        />
                      </div>
                    )}

                    {hasColorPalette && (
                      <div className="col-12">
                        <div className="flex flex-column gap-2">
                          <span className="text-sm text-500 line-height-1">
                            Paleta de colores
                          </span>
                          <div className="flex flex-wrap gap-2 align-items-center">
                            {FEATURED_BADGE_COLOR_VARIANT_OPTIONS.map((variant) => {
                              const swatch = getFeaturedBadgeSwatch(
                                colorSwatches,
                                colorSlug as FeaturedBadgeColorSlug,
                                variant
                              );
                              const isSelected = (entry.colorVariant ?? 1) === variant;
                              const stateLabel =
                                FEATURED_BADGE_STATES.find((s) => s.value === entry.slug)?.label ??
                                entry.slug;

                              return (
                                <button
                                  key={`${entry.key}-color-${variant}`}
                                  type="button"
                                  aria-pressed={isSelected}
                                  aria-label={`${stateLabel} variante ${variant}`}
                                  disabled={isSubmitting}
                                  onClick={() => handleEntryColorVariantChange(index, variant)}
                                  className="border-none cursor-pointer p-0 border-round-md bg-transparent"
                                  style={{
                                    outline: isSelected
                                      ? "2px solid var(--primary-color)"
                                      : "1px solid var(--surface-border)",
                                    outlineOffset: 2,
                                  }}
                                >
                                  <span
                                    className="inline-flex align-items-center justify-content-center border-round-md px-2 py-1 text-xs font-semibold"
                                    style={{
                                      backgroundColor: swatch.backgroundColor,
                                      color: swatch.textColor,
                                      minWidth: "5.5rem",
                                    }}
                                  >
                                    {stateLabel}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          <small className="text-500">
                            Variante 1 = color por defecto de la app.
                          </small>
                        </div>
                      </div>
                    )}

                    {slugConflict && (
                      <div className="col-12">
                        <small className="text-red-500">{slugConflict}</small>
                      </div>
                    )}
                  </div>
                </AccordionTab>
              );
            })}
          </Accordion>

          {canManageMultipleEntries && (
            <div className="mt-4">
              <Button
                type="button"
                label="Agregar otra regla"
                icon="pi pi-plus"
                severity="secondary"
                outlined
                onClick={handleAddEntry}
                disabled={isSubmitting || Boolean(ruleConflict) || values.entries.length >= FEATURED_BADGE_STATES.length}
              />
            </div>
          )}
        </div>

        {(errorMessage || ruleConflict) && (
          <div className="col-12">
            <small className="text-red-500">{errorMessage ?? ruleConflict}</small>
          </div>
        )}

        <div className="col-12 flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="flex flex-wrap align-items-center gap-3">
            <div className="flex flex-column gap-2">
              <span className="text-sm text-500 line-height-1">Activar/desactivar regla</span>
              <div className="flex align-items-center gap-2">
                <InputSwitch
                  inputId="featuredBadgeFormActive"
                  checked={Boolean(values.active)}
                  onChange={(event) => handleActiveChange(Boolean(event.value))}
                  disabled={isSubmitting || isDeletingGroup}
                />
              </div>
            </div>

            {mode === "edit" && onDelete && (
              <Button
                label="Eliminar regla"
                icon="pi pi-trash"
                severity="danger"
                outlined
                onClick={onDelete}
                disabled={isSubmitting || isDeletingGroup}
                loading={isDeletingGroup}
              />
            )}
          </div>

          <div className="flex gap-2">
            <Button
              label="Cancelar"
              severity="secondary"
              outlined
              onClick={onCancel}
              disabled={isSubmitting || isDeletingGroup}
            />
            <Button
              label={mode === "edit" ? "Guardar" : "Crear"}
              icon="pi pi-check"
              onClick={onSubmit}
              loading={isSubmitting}
              disabled={isSubmitting || isDeletingGroup || Boolean(ruleConflict) || values.entries.length === 0}
            />
          </div>
        </div>
      </div>
    </Panel>
  );
};
